import { useMemo, useRef, useState } from "react";
import { Button, Input, V2Label } from "web-design-system";
import {
  ArrowRightIcon,
  ArrowLeftIcon,
  XIcon,
  FileIcon,
  UploadIcon,
  LockIcon,
  Loader2,
} from "lucide-react";
import { useInvestorPrimaryBankAccount } from "creso-core/hooks";
import { StepProps, GiftKycFormData } from "../../../types";
import { fileNameFromUrl } from "../../../utils";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

type DocKey = keyof Pick<
  GiftKycFormData,
  "maskedAadhaar" | "panCard" | "cancelledCheque"
>;

const DOC_KEYS: DocKey[] = ["maskedAadhaar", "panCard", "cancelledCheque"];

const PASSWORD_FIELDS = {
  maskedAadhaar: "maskedAadhaarPassword",
  panCard: "panCardPassword",
  cancelledCheque: "cancelledChequePassword",
} as const satisfies Record<DocKey, keyof GiftKycFormData>;

// pdf.js is heavy, so it's only loaded once a PDF is actually uploaded.
const loadPdfjs = () => import("../pdfjs");

const isPdf = (file: File) =>
  file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

interface DocSection {
  key: DocKey;
  label: string;
  description: React.ReactNode;
  uploadLabel: string;
  subText?: React.ReactNode;
}

function getDocSections(bankDescription: React.ReactNode): DocSection[] {
  return [
    {
      key: "maskedAadhaar",
      label: "Masked Aadhaar",
      description: (
        <>
          The first 8 digits are blacked out and the last 4 digits are visible.{" "}
          <a
            className="font-semibold text-foreground"
            target="_blank"
            href="https://myaadhaar.uidai.gov.in/genricDownloadAadhaar/en"
            rel="noreferrer"
          >
            Click here to download
          </a>
        </>
      ),
      subText: (
        <span className="text-xs text-muted-foreground">
          Need further help?{" "}
          <a
            className="text-primary font-semibold"
            href="https://youtu.be/eZKnFYc9otA"
            target="_blank"
            rel="noreferrer"
          >
            Watch video
          </a>
        </span>
      ),
      uploadLabel: "Upload Masked Aadhaar",
    },
    {
      key: "panCard",
      label: "PAN Card",
      description: "A clear photo or scan of the PAN card.",
      uploadLabel: "Upload PAN Card",
    },
    {
      key: "cancelledCheque",
      label: "Bank Proof",
      description: bankDescription,
      uploadLabel: "Upload Cancelled Cheque",
    },
  ];
}

interface FileUploadAreaProps {
  uploadLabel: string;
  value: File | string | null;
  error?: string;
  onChange: (file: File | null) => void;
}

function FileUploadArea({
  uploadLabel,
  value,
  error,
  onChange,
}: FileUploadAreaProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file && file.size > MAX_FILE_SIZE) return;
    onChange(file);
    e.target.value = "";
  };

  const fileName = useMemo(() => {
    if (value instanceof File) {
      return value.name;
    }
    // URL of a document kept from a previous submission
    return fileNameFromUrl(value) ?? "";
  }, [value]);

  return (
    <div>
      {value ? (
        <div className="flex h-12 items-center justify-between rounded-lg border border-input bg-muted px-4 pr-2">
          <div className="flex items-center gap-2 min-w-0">
            <FileIcon className="size-4 shrink-0 text-muted-foreground" />
            <span className="text-sm text-foreground truncate">{fileName}</span>
          </div>
          <Button
            type="button"
            onClick={() => onChange(null)}
            className="ml-2 shrink-0"
            variant="ghost"
            size="icon"
          >
            <XIcon className="size-4" />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full flex h-12 items-center gap-3 rounded-lg border border-dashed border-muted-foreground/30 px-4 hover:bg-muted/40 transition-colors text-left"
        >
          <UploadIcon className="size-4 shrink-0 text-muted-foreground" />
          <span className="text-sm">
            <span className="text-foreground">{uploadLabel}</span>
            <span className="text-muted-foreground ml-1.5">
              jpg, png, or pdf (max 5mb)
            </span>
          </span>
        </button>
      )}
      {error && (
        <p className="mt-1 text-xs font-medium text-destructive">{error}</p>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.pdf"
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
}

export function DocumentsStep({ formData, onNext, onBack }: StepProps) {
  const { data: bankAccount } = useInvestorPrimaryBankAccount(
    formData.selectedClient?.investment_account ?? ""
  );

  const bankDescription = bankAccount ? (
    <>
      Passbook, bank statement, or cancelled cheque of{" "}
      <span className="font-semibold">{bankAccount.bank_name}</span> *
      {bankAccount.account_number.slice(-4)}
    </>
  ) : (
    "Passbook, bank statement, or cancelled cheque"
  );

  const docSections = getDocSections(bankDescription);

  const [files, setFiles] = useState<Record<DocKey, File | string | null>>({
    maskedAadhaar: formData.maskedAadhaar,
    panCard: formData.panCard,
    cancelledCheque: formData.cancelledCheque,
  });
  const [errors, setErrors] = useState<Partial<Record<DocKey, string>>>({});
  const [passwords, setPasswords] = useState<Partial<Record<DocKey, string>>>(
    () =>
      Object.fromEntries(
        DOC_KEYS.map((key) => [key, formData[PASSWORD_FIELDS[key]] ?? ""])
      )
  );
  const [lockedDocs, setLockedDocs] = useState<
    Partial<Record<DocKey, boolean>>
  >(() =>
    Object.fromEntries(
      DOC_KEYS.map((key) => [key, !!formData[PASSWORD_FIELDS[key]]])
    )
  );
  const [passwordErrors, setPasswordErrors] = useState<
    Partial<Record<DocKey, string>>
  >({});
  const [isVerifying, setIsVerifying] = useState(false);
  const latestFiles = useRef(files);

  const setFile = (key: DocKey) => (file: File | null) => {
    latestFiles.current = { ...latestFiles.current, [key]: file };
    setFiles((prev) => ({ ...prev, [key]: file }));
    setLockedDocs((prev) => ({ ...prev, [key]: false }));
    setPasswords((prev) => ({ ...prev, [key]: "" }));
    setPasswordErrors((prev) => ({ ...prev, [key]: undefined }));
    if (!file) return;

    setErrors((prev) => ({ ...prev, [key]: undefined }));
    if (!isPdf(file)) return;

    loadPdfjs()
      .then(({ checkPdfPassword }) => checkPdfPassword(file))
      .then((status) => {
        if (latestFiles.current[key] !== file) return;
        setLockedDocs((prev) => ({
          ...prev,
          [key]: status !== "unlocked",
        }));
      })
      .catch(() => {});
  };

  const setPassword = (key: DocKey) => (password: string) => {
    setPasswords((prev) => ({ ...prev, [key]: password }));
    setPasswordErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const verifyPdfPasswords = async () => {
    const pdfKeys = DOC_KEYS.filter((key) => {
      const file = files[key];
      return file && file instanceof File && isPdf(file);
    });
    if (pdfKeys.length === 0) return true;

    const { checkPdfPassword } = await loadPdfjs();
    const results = await Promise.all(
      pdfKeys
        .filter((k) => files[k] instanceof File)
        .map(
          async (key) =>
            [
              key,
              files[key] instanceof File
                ? await checkPdfPassword(
                    files[key]!,
                    passwords[key] || undefined
                  )
                : "unlocked",
            ] as const
        )
    );

    const newPasswordErrors: typeof passwordErrors = {};
    // eslint-disable-next-line
    for (const [key, status] of results) {
      if (status === "needs-password") {
        newPasswordErrors[key] = "Enter the password for this PDF";
      } else if (status === "incorrect-password") {
        newPasswordErrors[key] = "Incorrect password";
      }
    }
    setLockedDocs((prev) => ({
      ...prev,
      ...Object.fromEntries(
        Object.keys(newPasswordErrors).map((key) => [key, true])
      ),
    }));
    setPasswordErrors(newPasswordErrors);
    return Object.keys(newPasswordErrors).length === 0;
  };

  const handleContinue = async () => {
    const newErrors: typeof errors = {};
    if (!files.maskedAadhaar) newErrors.maskedAadhaar = "Required";
    if (!files.panCard) newErrors.panCard = "Required";
    if (!files.cancelledCheque) newErrors.cancelledCheque = "Required";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsVerifying(true);
    try {
      if (!(await verifyPdfPasswords())) return;
    } finally {
      setIsVerifying(false);
    }

    onNext({
      ...files,
      maskedAadhaarPassword: passwords.maskedAadhaar || undefined,
      panCardPassword: passwords.panCard || undefined,
      cancelledChequePassword: passwords.cancelledCheque || undefined,
    } as Partial<GiftKycFormData>);
  };

  return (
    <div className="flex flex-col h-full justify-between">
      <div className="flex flex-col gap-8">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            className="-ml-2"
            variant="ghost"
            size="icon"
            onClick={onBack}
          >
            <ArrowLeftIcon className="size-4" />
          </Button>
          <span className="text-2xl font-bold text-foreground">
            Upload <span className="text-gift-foreground">Documents</span>
          </span>
        </div>

        <div className="flex flex-col gap-6">
          {docSections.map((section) => (
            <div key={section.key} className="flex flex-col">
              <span className="text-sm font-bold mb-1 text-foreground">
                {section.label}
              </span>
              <span className="text-xs text-muted-foreground mb-3">
                {section.description}
              </span>
              <FileUploadArea
                uploadLabel={section.uploadLabel}
                value={files[section.key]}
                error={errors[section.key]}
                onChange={setFile(section.key)}
              />
              {files[section.key] && lockedDocs[section.key] && (
                <div className="mt-3 flex flex-col gap-1.5">
                  <V2Label
                    htmlFor={`${section.key}-password`}
                    className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground"
                  >
                    <LockIcon className="size-3.5" />
                    This PDF is password protected
                  </V2Label>
                  <Input
                    id={`${section.key}-password`}
                    type="password"
                    autoComplete="off"
                    placeholder="Enter PDF password"
                    value={passwords[section.key] ?? ""}
                    onChange={(e) => setPassword(section.key)(e.target.value)}
                  />
                  {passwordErrors[section.key] && (
                    <p className="text-xs font-medium text-destructive">
                      {passwordErrors[section.key]}
                    </p>
                  )}
                </div>
              )}
              <div className="mt-2">{section.subText}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center mt-6">
        <Button size="lg" onClick={handleContinue} disabled={isVerifying}>
          Continue
          {isVerifying ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <ArrowRightIcon className="size-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
